import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../AppIcon';
import Button from './Button';
import usePerformanceOptimization from '../../hooks/usePerformanceOptimization';
import { NAVIGATION_ITEMS, COMPANY_INFO } from '../../constants/company';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isLargeScreen, setIsLargeScreen] = useState(false);
  const location = useLocation();
  const { shouldReduceAnimations, getOptimizedProps } = usePerformanceOptimization();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    const handleResize = () => {
      setIsLargeScreen(window.innerWidth >= 1024);
    };

    // Set initial values
    handleResize();

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const isActivePath = (path) => {
    return location.pathname === path;
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-header">
      <div className="w-full px-4 sm:px-6 lg:px-8 pt-4">
        <motion.div
          className={`mx-auto max-w-7xl transition-all duration-500 ease-out ${
            isScrolled
              ? 'bg-white/95 backdrop-blur-xl shadow-2xl border border-border/50 rounded-2xl'
              : 'bg-white/90 backdrop-blur-md shadow-lg border border-border/30 rounded-2xl'
          }`}
          initial={{ y: -100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
        <div className="flex items-center justify-between h-14 sm:h-16 lg:h-18 px-6 sm:px-8">
          {/* Logo */}
          <Link 
            to="/homepage" 
            className="flex items-center space-x-3 hover:opacity-80 transition-opacity duration-200"
            onClick={closeMenu}
          >
            <div className="relative">
            <div className="w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 bg-white rounded-lg sm:rounded-xl flex items-center justify-center">
              <img src="/OnsLogo.png" alt="" className="w-full h-full object-contain" />
            </div>
            </div>
            <div className="hidden sm:block">
              <h1 className="text-lg sm:text-xl lg:text-xl font-bold font-heading text-foreground">
                Ons Mettle
              </h1>
              <p className="text-xs text-brand-primary font-body -mt-1">
                Corporate
              </p>
            </div>
          </Link>

          {/* Desktop Navigation - Hidden on mobile, visible on large screens */}
          {isLargeScreen && (
            <nav className="flex items-center space-x-1">
              {NAVIGATION_ITEMS.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`px-4 py-2 rounded-lg text-sm font-medium font-body transition-all duration-300 ${
                    isActivePath(item.path)
                      ? 'bg-brand-primary text-white'
                      : 'text-readable nav-link-hover'
                  }`}
                >
                  {item.name}
                </Link>
              ))}
            </nav>
          )}

          {/* Desktop CTA - Hidden on mobile, visible on large screens */}
          {isLargeScreen && (
            <div className="flex items-center space-x-3">
              <Button
                variant="outline"
                size="sm"
              >
                Free Assessment
              </Button>
              <Button
                variant="default"
                size="sm"
              >
                Get Started
              </Button>
            </div>
          )}

          {/* Mobile Menu Button - Visible on mobile, hidden on large screens */}
          {!isLargeScreen && (
            <button
              onClick={toggleMenu}
              className="p-2 rounded-lg border border-border hover:bg-muted transition-all duration-200"
              aria-label="Toggle menu"
            >
              <Icon
                name={isMenuOpen ? 'X' : 'Menu'}
                size={20}
                className="text-foreground"
              />
            </button>
          )}
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{
                duration: 0.2,
                ease: "easeOut"
              }}
              className="lg:hidden overflow-hidden border-t border-border"
            >
              <div className="bg-white">
                <nav className="px-4 py-6">
                  <div className="space-y-1">
                    {NAVIGATION_ITEMS.map((item) => (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={closeMenu}
                        className={`flex items-center space-x-3 px-4 py-3 rounded-lg text-base font-medium font-body transition-all duration-300 ${
                          isActivePath(item.path)
                            ? 'bg-brand-primary text-white'
                            : 'text-readable list-item-hover'
                        }`}
                      >
                        <Icon
                          name={item.icon}
                          size={20}
                          className={isActivePath(item.path) ? 'text-white' : 'text-brand-neutral'}
                        />
                        <span>{item.name}</span>
                      </Link>
                    ))}
                  </div>

                  {/* Mobile CTAs */}
                  <div className="mt-6 pt-6 border-t border-border space-y-3">
                    <Button
                      variant="outline"
                      fullWidth
                      className="justify-center"
                    >
                      Free Assessment
                    </Button>
                    <Button
                      variant="default"
                      fullWidth
                      className="justify-center"
                    >
                      Get Started
                    </Button>
                  </div>
                </nav>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        </motion.div>
      </div>
    </header>
  );
};

export default Header;